'use client'
import type { FC } from 'react'
import { useMemo, useState } from 'react'
import { Skeleton } from '@chakra-ui/react'
import { EmptyMessage, GrandSection } from 'ui'
import type { StorefrontWidget, WidgetBlog, WidgetBlogCategory } from 'ecosystem'
import { useBlogPostsBySlugRequest } from '../hooks'
import type { IBlogPostsProps } from './BlogPosts'
import { BlogNavigation, BlogPosts } from './index'

export interface BlogStdPageConfig {
  widgetBlogCategories: WidgetBlogCategory[]
  /**
   * Server-fetched previews of the first category's posts (`getBlogPostPreviewsBySlugs`).
   * Without them the server HTML has no posts, only the empty message, until the client fetch
   * returns, so crawlers that don't run JS see an empty blog.
   */
  initialBlogPosts?: StorefrontWidget<WidgetBlog>[]
  overrides?: {
    title?: string
    noBlogsMsg?: string
  }
  paths: {
    blogPostsBySlug: string
    blogPage?: string
  }
}

interface BlogPageStdProps extends Pick<IBlogPostsProps, 'customComponents'> {
  config: BlogStdPageConfig
}

const BlogPageStd: FC<BlogPageStdProps> = ({
  config: { overrides, widgetBlogCategories, initialBlogPosts, paths },
  customComponents
}) => {
  const [selectedCategory, setSelectedCategory] = useState<WidgetBlogCategory | null>(null)
  // The first category is the one BlogNavigation preselects, so its posts can come from the server.
  const showInitialPosts =
    !!initialBlogPosts && (!selectedCategory || selectedCategory.id === widgetBlogCategories[0]?.id)
  const relatedSlugs = useMemo(
    () =>
      showInitialPosts
        ? undefined
        : selectedCategory?.relatedBlogPosts.map((rbp) => rbp.slug || ''),
    [showInitialPosts, selectedCategory?.relatedBlogPosts]
  )
  const {
    data: fetchedBlogPosts,
    error,
    isLoading
  } = useBlogPostsBySlugRequest(paths.blogPostsBySlug, relatedSlugs)
  const blogPostsWidgets = showInitialPosts ? initialBlogPosts : fetchedBlogPosts

  if (error && !showInitialPosts) {
    return <EmptyMessage text="Oops! Something happened" />
  }

  return (
    <GrandSection
      contentContainerProps={{
        paddingTop: {
          base: 6,
          md: 4
        },
        spacing: {
          base: 8,
          md: 10
        }
      }}
      headingTag="h1"
      minH="800px"
      position="relative"
      sectionId="blog navigation"
      title={overrides?.title || 'Blogg'}
      titleProps={{
        containerProps: {
          margin: 0
        }
      }}
    >
      <BlogNavigation items={widgetBlogCategories} onSelect={setSelectedCategory} />
      <Skeleton isLoaded={showInitialPosts || !isLoading} minH="800px">
        {blogPostsWidgets ? (
          <BlogPosts customComponents={customComponents} widgets={blogPostsWidgets} />
        ) : (
          <EmptyMessage text={overrides?.noBlogsMsg || 'Inga inlägg hittades'} />
        )}
      </Skeleton>
    </GrandSection>
  )
}

export default BlogPageStd
